import React, { useEffect, useState, useCallback, Suspense } from 'react';
import { connect } from 'react-redux';
import 'antd/dist/antd.css';
import {
  getVehicle,
  deleteVehicle,
  getVehicleGroups,
  getVehicleModels,
  changeVehicleStatus,
} from 'store/requests/vehicle';
import { Modal, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import trTR from 'antd/es/locale/tr_TR';
import { CSVLink } from 'react-csv';
import { ConfigProvider } from 'antd';
import { debouncedDataLoadWaitMs, ORDER_TYPES } from 'helpers/constants';
import { Tooltip, Row, Button, Table, Col, Form, Tag, Dropdown } from 'antd';
import {
  ExclamationCircleOutlined,
  FilterOutlined,
  EditOutlined,
  MoreOutlined,
  PlusOutlined,
  DownloadOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';
import { getFirms } from '../../../../store/requests/definitions';
const Add = React.lazy(() => import('./Add'));
const Edit = React.lazy(() => import('./Edit'));
const VehiclesTableFilters = React.lazy(() => import('./VehiclesTableFilters'));
let lastQuery = false;

const VehicleView = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const { confirm } = Modal;
  const [filters, setFilters] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [defaultPlateNumberSort, setDefaultPlateNumberSort] = useState(true);
  const [vehicleGroups, setVehicleGroups] = useState([]);
  const [firms, setFirms] = useState([]);
  const [vehicleModels, setVehicleModels] = useState([]);
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [20, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 20,
    },
  });

  useEffect(() => {
    fetchData();
    setIsFiltersOpen(false);
    return () => {
      lastQuery = null;
      setLoading(false);
    };
  }, [JSON.stringify(tableParams), JSON.stringify(filters)]);

  useEffect(() => {
    (async () => {
      const vehicleGroupsRes = await getVehicleGroups();
      const vehicleModelsRes = await getVehicleModels();
      const firmsRes = await getFirms('&size=2000');
      setVehicleGroups(vehicleGroupsRes._embedded.vehicleGroupResponseDtoList);
      setFirms(firmsRes._embedded.firmResponseDtoList);
      setVehicleModels(vehicleModelsRes);
    })();
  }, []);

  const fetchData = () => {
    const queryString = getQueryString();
    loadData(queryString);
  };

  const columns = [
    {
      title: t('plateNumber'),
      dataIndex: 'plateNumber',
      sorter: true,
      showSorterTooltip: false,
      defaultSortOrder: defaultPlateNumberSort ? 'ascend' : null,
    },
    {
      title: t('capacityQuantity'),
      dataIndex: 'capacity',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('vehicleGroup'),
      dataIndex: 'vehicleGroup',
      sorter: true,
      showSorterTooltip: false,
      render: (vehicleGroup) => vehicleGroup?.name,
    },
    {
      title: t('vehicleModel'),
      dataIndex: 'vehicleModel',
      sorter: true,
      showSorterTooltip: false,
      render: (vehicleModel) => vehicleModel?.name,
    },
    {
      title: t('isActive'),
      dataIndex: 'active',
      sorter: true,
      showSorterTooltip: false,
      render: (active) =>
        active ? (
          <Tag color={'green'}>{t('active')}</Tag>
        ) : (
          <Tag color={'red'}>{t('passive')}</Tag>
        ),
    },
    {
      title: t('firm'),
      dataIndex: 'firm',
      sorter: true,
      showSorterTooltip: false,
      render: (firm) => firm?.name,
    },
    {
      width: 50,
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            menu={{
              items: [
                {
                  label: t('edit'),
                  icon: <EditOutlined />,
                  onClick: () => {
                    setIsEditModalOpen(true);
                  },
                },
                {
                  danger: dataItem.active,
                  label: dataItem.active ? t('delete') : t('activate'),
                  icon: dataItem.active ? <CloseOutlined /> : <CheckOutlined />,
                  onClick: () => {
                    showChangeVehicleStatus({
                      vehicleIds: [selectedItem.id],
                      status: !dataItem.active,
                    });
                  },
                },
              ],
            }}
            placement="bottomRight"
            arrow
            trigger={['click']}
            onClick={() => setSelectedItem(dataItem)}
          >
            <Button shape="circle" icon={<MoreOutlined />}></Button>
          </Dropdown>
        ),
    },
  ];

  const actions = [
    {
      isCrudEnabled,
      type: 'primary',
      title: t('add'),
      icon: <PlusOutlined />,
      onClick: () => setIsAddModalOpen(true),
      disabled: false,
    },
    {
      isCrudEnabled: true,
      type: '',
      title: t('download'),
      customContent: (
        <CSVLink
          filename={'Vehicles'}
          data={[
            columns.map((item) => item.title),
            ...rows.map((item) => [
              item.plateNumber,
              item.capacity,
              item.vehicleGroup ? item.vehicleGroup.name : '',
              item.vehicleModel ? item.vehicleModel.name : '',
              item.firm ? item.firm.name : '',
            ]),
          ]}
        >
          <Button
            style={{ marginRight: '3px' }}
            shape="round"
            icon={<DownloadOutlined />}
          ></Button>
        </CSVLink>
      ),
      onClick: () => {},
      disabled: false,
    },
  ];

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      return (
        <Tooltip title={item.title} key={idx}>
          {item.isCrudEnabled &&
            (item.customContent ?? (
              <Button
                type={item?.type}
                shape="round"
                icon={item?.icon}
                onClick={item.onClick}
                disabled={item.disabled}
                style={{ marginBottom: '10px', marginRight: '3px' }}
                danger={item?.danger}
              />
            ))}
        </Tooltip>
      );
    });
  };

  function showChangeVehicleStatus(data) {
    confirm({
      title: t('modalDeleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: !data.status ? t('vehicleDelete') : t('vehicleActive'),
      okText: !data.status ? t('delete') : t('activate'),
      okType: !data.status ? 'danger' : 'primary',
      cancelText: t('cancel'),
      centered: true,
      onOk() {
        changeVehicleStatus(data).then((res) => {
          if (res.isSuccess) {
            notificationDelete('success');
            lastQuery = null;
            const queryString = getQueryString();
            loadData(queryString);
          } else {
            notification.error({ message: res.message });
          }
        });
      },
      onCancel() {},
    });
  }

  const renderSelectedFilters = () => {
    return Object.keys(filters).map((item, idx) => {
      const vehicleGroup = vehicleGroups.find(
        (group) => group.id === filters?.vehicleGroup,
      );
      const firm = firms.find((firm) => firm.id === filters?.firm);
      const vehicleModel = vehicleModels.find(
        (model) => model.id === filters?.vehicleModel,
      );
      if (filters[item]) {
        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {`${t(item)} : ${t(`${item === 'vehicleGroup' ? vehicleGroup?.name : item === 'firm' ? firm?.name : item === 'vehicleModel' ? vehicleModel?.name : filters[item]}`)}`}
          </Tag>
        );
      }
    });
  };
  const notificationDelete = (type) => {
    notification[type]({
      message: t('processCompleted'),
      description: t('vehicleStatusChanged'),
    });
  };

  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filters).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filters,
          [destinationItem]: undefined,
        };
        setFilters(editedFilters);
        form.setFieldsValue(editedFilters);
        setDefaultPlateNumberSort(true);
      }
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      ...sorter,
    });
  };

  const getQueryString = () => {
    let queryString = '';

    if (Object.values(filters).some((item) => !!item)) {
      let firstFilter = true;
      Object.keys(filters).forEach((item, idx) => {
        if (filters[item]) {
          const filterString = `${item === 'firm' ? 'firm.id' : item === 'vehicleGroup' ? 'vehicleGroup.id' : item === 'vehicleModel' ? 'vehicleModel.id' : item}==${encodeURI(filters[item])}`;
          if (firstFilter) {
            queryString += `filter=${filterString}`;
            firstFilter = false;
          } else {
            queryString += ` and ${filterString}`;
          }
        }
      });
    }

    if (defaultPlateNumberSort) {
      queryString = `${queryString}&sort=plateNumber,asc`;
      setDefaultPlateNumberSort(false);
    }

    queryString = `${queryString}&page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;
    if (tableParams.column) {
      queryString = `${queryString}&sort=${tableParams.column.dataIndex},${ORDER_TYPES[tableParams.order]}`;
    }
    return queryString;
  };

  async function loadDataWithDebounce(queryString) {
    if (!loading) {
      lastQuery = queryString;
      setLoading(true);
      getVehicle(queryString)
        .then((vehicleRequest) => {
          const { _embedded, page } = vehicleRequest;
          const { vehicleResponseDtoList } = _embedded ? _embedded : [];
          setRows(vehicleResponseDtoList ? vehicleResponseDtoList : []);
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: page.totalElements,
            },
          });
        })
        .finally(() => setLoading(false));
    }
  }

  const loadData = useCallback(
    debounce((queryString) => {
      loadDataWithDebounce(queryString);
    }, debouncedDataLoadWaitMs),
  );

  return (
    <div style={{ position: 'relative' }}>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col>{renderActionButtons()}</Col>
        <Col>
          <Button
            shape="round"
            icon={<FilterOutlined />}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            {t('filters')}
          </Button>
        </Col>
      </Row>
      <Row style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {renderSelectedFilters()}
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) => record.id}
            scroll={{
              y: '70vh',
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={<></>}>
        {isFiltersOpen && (
          <VehiclesTableFilters
            setIsFiltersOpen={setIsFiltersOpen}
            setFilters={setFilters}
            form={form}
            vehicleGroups={vehicleGroups}
            firms={firms}
            vehicleModels={vehicleModels}
          />
        )}
        {isAddModalOpen && (
          <Add
            onCloseHandler={() => setIsAddModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
        {isEditModalOpen && (
          <Edit
            vehicleId={selectedItem.id}
            onCloseHandler={() => setIsEditModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
      </Suspense>
    </div>
  );
};

export default connect()(VehicleView);
